import Icon from '../components/Icon';
import './Pages.css';

const layers = [
  {
    num: '01',
    icon: 'sos' as const,
    title: 'Emergency Device Layer',
    desc: 'Physical SOS devices designed for the moment of stress. One button starts everything.',
    items: ['SOS button + status LEDs', 'ESP32-class microcontroller', 'LoRa communication module', 'GPS / location module', 'Battery / power system', 'Rugged, weather-ready enclosure'],
  },
  {
    num: '02',
    icon: 'lora' as const,
    title: 'LoRa Communication Layer',
    desc: 'Long-range, low-power radio carries essential alert data through mountains and forests.',
    items: ['Device ID', 'Emergency status', 'GPS coordinates', 'Timestamp', 'Battery & sensor status', 'Alert type / category'],
  },
  {
    num: '03',
    icon: 'gateway' as const,
    title: 'Gateway Layer',
    desc: 'Bridges the local LoRa network to the central platform when backhaul is available.',
    items: ['Receives LoRa packets', 'Buffers during outages', 'Forwards via internet / backhaul', 'Solar / battery ready', 'Remote monitoring', 'Multi-channel coverage'],
  },
  {
    num: '04',
    icon: 'cloud' as const,
    title: 'Digital Response Platform',
    desc: 'A web-based dashboard for real-time monitoring, mapping, and incident response.',
    items: ['Real-time alerts', 'Map-based visualization', 'Ward-level filtering', 'Status tracking (5 states)', 'Responder assignment', 'Incident management'],
  },
];

const stack = [
  { title: 'Hardware', items: ['ESP32 MCU', 'LoRa module', 'GPS module', 'SOS button', 'Battery system', 'LoRa gateway'] },
  { title: 'Communication', items: ['LoRa (sub-GHz)', 'LoRaWAN-ready', 'Long range', 'Low power', 'License-free ISM bands', 'Mesh-capable'] },
  { title: 'Backend', items: ['Device registry', 'SOS event store', 'Alert state machine', 'Location service', 'Responder system', 'Audit trail'] },
  { title: 'Frontend', items: ['React + TypeScript', 'Real-time dashboard', 'Map visualization', 'Ward filtering', 'Mobile-friendly', 'Offline-first capable'] },
];

export default function Technology() {
  return (
    <div className="page">
      <section className="page-hero page-hero--dark">
        <div className="container">
          <span className="eyebrow">System Architecture</span>
          <h1>Four modular layers. One resilient bridge.</h1>
          <p className="lede">Each component is independently upgradeable, the architecture can evolve without rebuilding the whole system.</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="tech-layers">
            {layers.map((l, i) => (
              <article key={l.num} className="tech-layer reveal" style={{ animationDelay: `${i * 80}ms` }}>
                <div>
                  <div className="tech-layer__num">{l.num}</div>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon name={l.icon} size={22} />
                    {l.title}
                  </h3>
                  <p>{l.desc}</p>
                </div>
                <ul className="tech-list">
                  {l.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--soft">
        <div className="container">
          <div className="section__head">
            <div>
              <span className="eyebrow">Technology Stack</span>
              <h2>Affordable, open, and field-proven.</h2>
            </div>
            <p className="lede">
              The prototype architecture uses low-cost, widely available components and open
              standards. Nothing is exotic, and that is the point.
            </p>
          </div>
          <div className="tech-stack-grid">
            {stack.map((s) => (
              <div key={s.title} className="tech-stack-card">
                <h4>{s.title}</h4>
                <ul>{s.items.map((i) => <li key={i}>{i}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
